/**
 * editor2/shared/InlineEditField.tsx
 * クリックでその場で編集できるフィールド
 * text, textarea, number, select, email, tel に対応
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';

interface SelectOption {
  label: string;
  value: string | number;
}

interface InlineEditFieldProps {
  label: string;
  value: any;
  field: string;
  onChange?: (updates: Record<string, any>) => void;
  disabled?: boolean;
  type?: 'text' | 'textarea' | 'number' | 'email' | 'tel' | 'select';
  options?: SelectOption[];
  suffix?: string;
  displayValue?: string;
  /** select用: 選択肢のラベルではなく値のバッジ表示 */
  renderBadge?: (value: any) => React.ReactNode;
  placeholder?: string;
}

export default function InlineEditField({
  label,
  value,
  field,
  onChange,
  disabled = false,
  type = 'text',
  options,
  suffix,
  displayValue,
  renderBadge,
  placeholder,
}: InlineEditFieldProps) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState<string>('');
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(null);

  // 編集開始
  const startEdit = useCallback(() => {
    if (disabled) return;
    setEditValue(value != null ? String(value) : '');
    setEditing(true);
  }, [disabled, value]);

  // 編集確定
  const commitEdit = useCallback(() => {
    setEditing(false);
    if (!onChange) return;

    let newValue: any = editValue;
    if (type === 'number') {
      newValue = editValue === '' ? null : Number(editValue);
    }

    // 値が変わった場合のみonChange
    if (String(newValue ?? '') !== String(value ?? '')) {
      onChange({ [field]: newValue });
    }
  }, [editValue, value, field, onChange, type]);

  // キャンセル
  const cancelEdit = useCallback(() => {
    setEditing(false);
  }, []);

  // キーハンドラ
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && type !== 'textarea') {
        e.preventDefault();
        commitEdit();
      } else if (e.key === 'Escape') {
        cancelEdit();
      }
    },
    [commitEdit, cancelEdit, type],
  );

  // フォーカス
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current instanceof HTMLInputElement || inputRef.current instanceof HTMLTextAreaElement) {
        inputRef.current.select();
      }
    }
  }, [editing]);

  // select の場合は即座にコミット
  const handleSelectChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newVal = e.target.value;
      setEditValue(newVal);
      setEditing(false);
      if (onChange) {
        onChange({ [field]: type === 'number' ? Number(newVal) : newVal });
      }
    },
    [onChange, field, type],
  );

  // 表示テキスト
  const getDisplayText = () => {
    if (displayValue != null) return displayValue;
    if (renderBadge) return null; // badgeが描画する
    if (type === 'select' && options) {
      const opt = options.find((o) => String(o.value) === String(value));
      if (opt) return opt.label;
    }
    if (value != null && value !== '') {
      if (type === 'number' && typeof value === 'number') {
        return `${value.toLocaleString()}${suffix ? ` ${suffix}` : ''}`;
      }
      return `${String(value)}${suffix ? ` ${suffix}` : ''}`;
    }
    return '-';
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    fontSize: '0.9rem',
    border: '2px solid #3b82f6',
    borderRadius: 4,
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  };

  // select型は常にセレクト表示（クリックで切り替え不要）
  if (type === 'select' && options) {
    const selectStyle: React.CSSProperties = {
      width: '100%',
      padding: '8px 12px',
      fontSize: '0.9rem',
      border: '1px solid #e2e8f0',
      borderRadius: 4,
      outline: 'none',
      fontFamily: 'inherit',
      boxSizing: 'border-box',
      background: disabled ? '#f8fafc' : '#fff',
      color: '#334155',
      cursor: disabled ? 'default' : 'pointer',
    };

    return (
      <div className="gc-field">
        {label && (
          <label className="gc-label" style={{ fontSize: '0.85rem', color: '#64748b' }}>
            {label}
          </label>
        )}
        {renderBadge && (
          <div style={{ marginBottom: 4 }}>{renderBadge(value)}</div>
        )}
        <select
          value={value != null ? String(value) : ''}
          onChange={(e) => {
            const newVal = e.target.value;
            if (onChange) {
              onChange({ [field]: newVal === '' ? null : newVal });
            }
          }}
          disabled={disabled}
          style={selectStyle}
        >
          <option value="">-- 選択 --</option>
          {options.map((opt) => (
            <option key={String(opt.value)} value={String(opt.value)}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (editing) {
    return (
      <div className="gc-field">
        <label className="gc-label" style={{ fontSize: '0.85rem', color: '#3b82f6', fontWeight: 600 }}>
          {label}
        </label>
        {type === 'textarea' ? (
          <textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={handleKeyDown}
            rows={3}
            style={{ ...inputStyle, resize: 'vertical' }}
            placeholder={placeholder}
          />
        ) : (
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type={type === 'number' ? 'number' : type === 'email' ? 'email' : type === 'tel' ? 'tel' : 'text'}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={handleKeyDown}
            style={inputStyle}
            placeholder={placeholder}
          />
        )}
      </div>
    );
  }

  return (
    <div className="gc-field">
      <label className="gc-label" style={{ fontSize: '0.85rem', color: '#64748b' }}>
        {label}
      </label>
      <div
        className="gc-input"
        onClick={disabled ? undefined : startEdit}
        role={disabled ? undefined : 'button'}
        tabIndex={disabled ? undefined : 0}
        onKeyDown={
          disabled
            ? undefined
            : (e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  startEdit();
                }
              }
        }
        style={{
          background: disabled ? '#f8fafc' : '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: 4,
          padding: '8px 12px',
          minHeight: 38,
          display: 'flex',
          alignItems: 'center',
          color: '#334155',
          cursor: disabled ? 'default' : 'pointer',
          transition: 'border-color 0.15s',
        }}
      >
        <span style={{ flex: 1 }}>
          {renderBadge ? renderBadge(value) : getDisplayText()}
        </span>
      </div>
    </div>
  );
}
